import { cn } from "@design-system/utils";
import {
  forwardRef,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
} from "react";

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  autoExpand?: boolean;
};

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoExpand = true, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const handleResize = useCallback(() => {
      if (!textareaRef.current || !autoExpand) return;

      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }, [autoExpand]);

    useEffect(() => {
      if (!autoExpand) return;

      handleResize();
      // Add resize observer for dynamic content changes
      const resizeObserver = new ResizeObserver(handleResize);
      if (textareaRef.current) {
        resizeObserver.observe(textareaRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [autoExpand, handleResize]);

    return (
      <textarea
        className={cn(
          "flex min-h-[60px] w-full rounded-md border border-input bg-background dark:bg-gray-800 dark:border-gray-600 px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground dark:placeholder:text-gray-400 text-foreground dark:text-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={element => {
          // Handle both refs
          textareaRef.current = element;
          if (typeof ref === "function") {
            ref(element);
          } else if (ref) {
            ref.current = element;
          }
        }}
        onInput={handleResize}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
